import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Copy, ExternalLink, Mail, Upload, FileText, Eye, Edit, Home, Sparkles, Share2, Clock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Separator } from '../ui/Separator';
import { useAnonymousReviewContext } from './AnonymousReviewProvider';

const AnonymousFinalPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, clearData } = useAnonymousReviewContext();
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(true);

  // Use real review links from the created review
  const reviewLink = data.reviewLink 
    ? `${window.location.origin}/review/${data.reviewLink}` 
    : `${window.location.origin}/review/demo-link`;
  const creatorLink = data.reviewLink 
    ? `${window.location.origin}/review/${data.reviewLink}/creator` 
    : `${window.location.origin}/review/demo-link/creator`;
  const reviewId = data.tempReviewId || Math.floor(Math.random() * 10000);

  useEffect(() => {
    // Hide celebration animation after 3 seconds
    const timer = setTimeout(() => setShowCelebration(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(type);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleStartNewReview = () => {
    clearData();
    navigate('/anonymous-review');
  };

  const handleGoToDashboard = () => {
    clearData();
    navigate('/');
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Success Header with Animation */}
      <div className="text-center mb-8 relative">
        {showCelebration && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Sparkles className="w-8 h-8 text-yellow-500 animate-pulse" />
          </div>
        )}
        <div className="flex items-center justify-center mb-4">
          <CheckCircle className="w-16 h-16 text-green-500 mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              🎉 Review Created Successfully!
            </h1>
            <p className="text-muted-foreground">
              Your review is ready and notifications have been sent
            </p>
          </div>
        </div>
        
        <div className="flex items-center justify-center gap-4 mt-4">
          <Badge variant="outline" className="flex items-center gap-1">
            <FileText className="w-3 h-3" />
            Review #{reviewId}
          </Badge>
          <Badge variant="outline" className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Created {new Date().toLocaleDateString()}
          </Badge>
        </div>
      </div>

      <div className="space-y-6">
        {/* Review Summary */}
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-800">Review Summary</CardTitle>
            <CardDescription className="text-green-700">
              Here's what was created for your review: "{data.title}"
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-green-800">Your Email:</p>
                <p className="text-green-700">{data.creatorEmail}</p>
              </div>
              <div>
                <p className="font-medium text-green-800">Reviewer Email:</p>
                <p className="text-green-700">{data.reviewerEmail}</p>
              </div>
              {data.deadlineEnabled && data.deadline && (
                <div>
                  <p className="font-medium text-green-800">Deadline:</p>
                  <p className="text-green-700">{new Date(`${data.deadline}T${data.deadlineTime}`).toLocaleDateString()}</p>
                </div>
              )}
              {data.files.length > 0 && (
                <div>
                  <p className="font-medium text-green-800">Files:</p>
                  <p className="text-green-700">{data.files.length} file(s) uploaded</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Review Links */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Your Review Links
            </CardTitle>
            <CardDescription>
              Share these links to give access to your review
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Reviewer Link */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-600" />
                <h4 className="font-medium">Reviewer Link</h4>
                <Badge variant="secondary">For Reviewer</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Send this link to your reviewer ({data.reviewerEmail}) to let them review your files
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-100 p-2 rounded text-sm font-mono break-all">
                  {reviewLink}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(reviewLink, 'reviewer')}
                  className="shrink-0"
                >
                  {copiedLink === 'reviewer' ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(reviewLink, '_blank')}
                  className="shrink-0"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Creator Link */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Edit className="w-4 h-4 text-purple-600" />
                <h4 className="font-medium">Creator Link (Your Link)</h4>
                <Badge variant="secondary">For You</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Use this link to view responses, upload additional files, and manage your review
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-100 p-2 rounded text-sm font-mono break-all">
                  {creatorLink}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(creatorLink, 'creator')}
                  className="shrink-0"
                >
                  {copiedLink === 'creator' ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(creatorLink, '_blank')}
                  className="shrink-0"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              What Happens Next?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600 mt-0.5">
                  1
                </div>
                <div>
                  <p className="font-medium">Email Notifications Sent</p>
                  <p className="text-sm text-muted-foreground">
                    Both you and your reviewer have received email notifications with the review links
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600 mt-0.5">
                  2
                </div>
                <div>
                  <p className="font-medium">Upload Files (Optional)</p>
                  <p className="text-sm text-muted-foreground">
                    {data.files.length > 0 
                      ? "Your files are already uploaded and ready for review"
                      : "Use your creator link to upload files when ready"
                    }
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600 mt-0.5">
                  3
                </div>
                <div>
                  <p className="font-medium">Reviewer Provides Feedback</p>
                  <p className="text-sm text-muted-foreground">
                    Your reviewer will use their link to review your files and provide feedback
                  </p>
                </div>
              </div>

              {data.reminderEnabled && data.reminders.length > 0 && (
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600 mt-0.5">
                    4
                  </div>
                  <div>
                    <p className="font-medium">Automatic Reminders</p>
                    <p className="text-sm text-muted-foreground">
                      {data.reminders.length} reminder(s) are set up to keep your review on track
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* File Upload Reminder */}
        {data.files.length === 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <Upload className="w-5 h-5" />
                Don't Forget to Upload Your Files!
              </CardTitle>
              <CardDescription className="text-amber-700">
                Your review is created, but you haven't uploaded any files yet
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-amber-700 mb-4">
                Use your creator link above to upload the files you want reviewed. Your reviewer will be notified once files are available.
              </p>
              <Button
                variant="outline"
                onClick={() => window.open(creatorLink, '_blank')}
                className="border-amber-300 text-amber-800 hover:bg-amber-100"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Files Now
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button onClick={handleStartNewReview} variant="outline">
            <FileText className="w-4 h-4 mr-2" />
            Create Another Review
          </Button>
          <Button onClick={handleGoToDashboard}>
            <Home className="w-4 h-4 mr-2" />
            Go to Dashboard
          </Button>
        </div>

        {/* Success Footer */}
        <div className="text-center py-8">
          <p className="text-muted-foreground text-sm">
            🎉 Congratulations! Your review is live and ready. 
            <br />
            Keep your links safe - you'll need them to access your review.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnonymousFinalPage; 